import { BillDetailed } from '@enview/interface/types/bills/Bill';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BillAPI } from '../../api';
import { getTeamMode } from '../../dux';
import { getBillAlias } from '../../helpers/BillHelper';
import { g as abilityGlossary } from '../../config/ability';
import GateTooltip, { GateTooltipFeature } from '../Shared/Tooltips/TooltipGate';
import Can from '../../components/Can';
import ShareBillAliasButton from './ShareBillAliasButton';

type BillAliasFieldProps = {
  bill: BillDetailed;
};

const BillAliasField = (props: BillAliasFieldProps): ReactElement => {
  const { bill } = props;
  const teamModeId = useSelector(getTeamMode);
  const alias = getBillAlias(bill.userAliases, teamModeId);
  const [setBillAlias] = BillAPI.endpoints.setBillAlias.useMutation();
  const [clearBillAlias] = BillAPI.endpoints.clearBillAlias.useMutation();
  const { t } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);
  const [editedAlias, setEditedAlias] = useState(alias ? alias.alias : '');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isPopoverShowing, setIsPopoverShowing] = useState(false);

  useEffect(() => {
    // Focus on input field when it is rendered
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef.current, isEditing]);

  const handleClick = (): void => {
    setIsPopoverShowing(true);
    setIsEditing(true);
  };

  const handleBlur = async (): Promise<void> => {
    setIsEditing(false);
    await saveAlias();
  };

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (event.key === 'Enter') {
      setIsEditing(false);
      await saveAlias();
    } else if (event.key === 'Escape') {
      setIsEditing(false);
      setEditedAlias(alias?.alias ?? '');
    }
  };

  const saveAlias = async (): Promise<void> => {
    setIsLoading(true);
    const trimmedAlias = editedAlias.trim();
    if (alias && trimmedAlias === '') {
      await clearBillAlias({ bill, alias });
    } else if (trimmedAlias !== '' && (!alias || alias.alias !== trimmedAlias)) {
      const newAlias = {
        ...(alias ?? {}),
        teamId: teamModeId,
        billId: bill.id,
        alias: trimmedAlias,
      };
      await setBillAlias({ bill, alias: newAlias });
    }

    setIsLoading(false);
  };

  const getIconColor = (): string => {
    if (!alias?.alias || isEditing) {
      return '#979797';
    }
    return '#F79728';
  };

  const inputField = useMemo(
    () => (
      <input
        className="text-field"
        onBlur={handleBlur}
        onChange={(e) => setEditedAlias(e.target.value)}
        onKeyDown={handleKeyDown}
        ref={inputRef}
        type="text"
        value={editedAlias}
      />
    ),
    [editedAlias, inputRef.current, isEditing],
  );

  const displayButton = useMemo(
    () => (
      <button
        className={`btn-no-styles ${!alias?.alias ? 'empty-field' : 'bill-alias'}`}
        id={`bill-alias-${bill.id}`}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        type={'button'}
      >
        {alias ? alias.alias : t('bill.header.aliasFieldPlaceholder')}
      </button>
    ),
    [alias, editedAlias, isEditing],
  );

  const loadingIndicator = isLoading && (
    <Spinner
      animation="border"
      className="text-field-spinner"
      role="status"
      variant="secondary"
    />
  );

  return (
    <>
      <Can I={abilityGlossary.CREATE} a={abilityGlossary.BILL_SHORTNAME} passThrough>
        {(can: boolean) => (
          <>
            <div className="d-flex flex-row flex-wrap shortname-row">
              <FontAwesomeIcon
                color={getIconColor()}
                icon={faPen}
                onClick={handleClick}
              />
              {can && isEditing ? inputField : displayButton}
              {can && alias && !isEditing && (
                <ShareBillAliasButton alias={alias} bill={bill} />
              )}
              {loadingIndicator}
            </div>
            {!can && isPopoverShowing && (
              <GateTooltip
                accountType="pro"
                anchorId={`bill-alias-${bill.id}`}
                customContent={
                  <Trans
                    components={{
                      tagLink: <a href={t('urls.aliasBill')} target="_blank" />,
                      gate: <p className="tooltip-header" />,
                    }}
                    i18nKey="featureGating.tooltipText.createAlias"
                    key="featureGating.tooltipText.createAlias"
                  />
                }
                featureName={GateTooltipFeature.BillAlias}
                place="right"
              />
            )}
          </>
        )}
      </Can>
    </>
  );
};

export default BillAliasField;
